import type { KpiScore } from '@/types/evaluation.ts';
import type { VendorProfile, VendorKpiCategory, VendorKpiCategoryId } from '@/types/vendor-kpi.ts';
import { VENDOR_CATEGORIES, KPI_TEMPLATE } from '@/types/vendor-kpi.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';
import i18n from '@/i18n.ts';

/**
 * Maps backend KPI ids (k1...k14) to frontend template ids.
 * Backend persists scores keyed by k-ids; the template uses semantic ids.
 */
const BACKEND_ID_TO_TEMPLATE_ID: Record<string, string> = {
  k1: 'prg_escucha',
  k2: 'pit_duracion',
  k3: 'cie_primervalor',
  k4: 'cie_nextstep',
  k5: 'prg_preguntas',
  k6: 'prg_confirmaciones',
  k7: 'emp_engagement',
  k8: 'pit_beneficios',
  k9: 'pit_lexico',
  k10: 'emp_reformulaciones',
  k14: 'neg_precio',
};

/**
 * Build a VendorProfile from old-format KPI scores (0-10).
 * Maps each KPI to the new 6-category system, scaling scores to 0-99.
 */
export function buildVendorProfileFromKpis(kpis: KpiScore[]): VendorProfile {
  // Map old kpi IDs to new category IDs
  const oldToNewCategory: Record<string, VendorKpiCategoryId> = {
    // estructura_conversacion → split between PRG (talk/listen) and CIE (pitch, next step, primer valor)
    k1: 'PRG', // Talk/Listen Ratio
    k2: 'PIT', // Duración pitch inicial
    k3: 'CIE', // Tiempo al primer valor
    k4: 'CIE', // Propuesta de next step
    // tecnica_preguntas → PRG + EMP
    k5: 'PRG', // Preguntas por minuto
    k6: 'PRG', // Confirmaciones de interés
    k7: 'EMP', // Empatía/engagement
    // argumentacion → PIT + EMP
    k8: 'PIT', // Beneficios vs Características
    k9: 'PIT', // Adecuación léxica
    k10: 'EMP', // Reformulaciones eficaces
    // k11/k12/k13 (comunicacion_verbal) removed from MVP — backend filters them too
    // gestion_comercial → NEG
    k14: 'NEG', // Gestión de precio
  };

  const categoryIds = Object.keys(VENDOR_CATEGORIES) as VendorKpiCategoryId[];

  // Collect scores per new category from old KPIs
  const scoresByCategory: Record<VendorKpiCategoryId, { total: number; count: number }> = {} as never;
  for (const catId of categoryIds) {
    scoresByCategory[catId] = { total: 0, count: 0 };
  }
  for (const kpi of kpis) {
    const newCat = oldToNewCategory[kpi.id];
    if (!newCat) continue;
    if (kpi.type === 'text' || kpi.type === 'yesno') {
      // Use score directly (0-10)
      scoresByCategory[newCat].total += kpi.score;
      scoresByCategory[newCat].count += 1;
    } else {
      scoresByCategory[newCat].total += kpi.score;
      scoresByCategory[newCat].count += 1;
    }
  }

  const categories: VendorKpiCategory[] = categoryIds.map((catId) => {
    const meta = VENDOR_CATEGORIES[catId];
    const templateKpis = KPI_TEMPLATE.filter((t) => t.category === catId);

    const catData = scoresByCategory[catId];
    const catScore99 = catData.count > 0 ? toScore99(catData.total / catData.count) : 0;

    const kpiDetails = templateKpis.map((t) => {
      const matchingKpi = kpis.find(
        (k) =>
          oldToNewCategory[k.id] === catId &&
          (BACKEND_ID_TO_TEMPLATE_ID[k.id] === t.id || k.id === t.id),
      );
      const score = matchingKpi ? toScore99(matchingKpi.score) : catScore99;
      return { id: t.id, label: i18n.t(t.labelKey), score, description: i18n.t(t.descriptionKey) };
    });

    const finalScore = catData.count > 0 ? toScore99(catData.total / catData.count) : 0;

    return { id: catId, name: i18n.t(meta.nameKey), shortName: meta.shortName, kpis: kpiDetails, score: finalScore };
  });

  const presentScores: number[] = [];
  for (const kpi of kpis) {
    if (oldToNewCategory[kpi.id]) presentScores.push(kpi.score);
  }
  const globalScore = presentScores.length > 0
    ? toScore99(presentScores.reduce((s, x) => s + x, 0) / presentScores.length)
    : 0;

  return { globalScore, categories };
}

/**
 * Build a VendorProfile from backend KPI averages JSON.
 * Input format: [{ "id": "k1", "score": 7.2 }, ...]
 * The "id" can be old-format (k1-k14) or new-format (com_velocidad, etc.).
 */
export function buildVendorProfileFromAverages(kpiAveragesJson: string): VendorProfile | null {
  if (!kpiAveragesJson || kpiAveragesJson === '[]') return null;

  let parsed: { id: string; score: number }[];
  try {
    parsed = JSON.parse(kpiAveragesJson);
  } catch {
    return null;
  }

  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  // Map old kpi IDs (k1-k14) to new category IDs
  const oldToNewCategory: Record<string, VendorKpiCategoryId> = {
    k1: 'PRG', k2: 'PIT', k3: 'CIE', k4: 'CIE',
    k5: 'PRG', k6: 'PRG', k7: 'EMP',
    k8: 'PIT', k9: 'PIT', k10: 'EMP',
    // k11/k12/k13 (acoustic) removed from MVP
    k14: 'NEG',
  };

  // Map new kpi IDs to category IDs (from KPI_TEMPLATE)
  const newIdToCategory: Record<string, VendorKpiCategoryId> = {};
  for (const t of KPI_TEMPLATE) {
    newIdToCategory[t.id] = t.category;
  }

  const categoryIds = Object.keys(VENDOR_CATEGORIES) as VendorKpiCategoryId[];
  const scoresByCategory: Record<VendorKpiCategoryId, { total: number; count: number }> = {} as never;
  for (const catId of categoryIds) {
    scoresByCategory[catId] = { total: 0, count: 0 };
  }

  for (const kpi of parsed) {
    const cat = oldToNewCategory[kpi.id] ?? newIdToCategory[kpi.id];
    if (!cat) continue;
    scoresByCategory[cat].total += kpi.score;
    scoresByCategory[cat].count += 1;
  }

  const categories: VendorKpiCategory[] = categoryIds.map((catId) => {
    const meta = VENDOR_CATEGORIES[catId];
    const templateKpis = KPI_TEMPLATE.filter((t) => t.category === catId);
    const catData = scoresByCategory[catId];
    const catScore99 = catData.count > 0 ? toScore99(catData.total / catData.count) : 0;

    const kpiDetails = templateKpis.map((t) => {
      const specific = parsed.find(
        (p) => BACKEND_ID_TO_TEMPLATE_ID[p.id] === t.id || p.id === t.id,
      );
      const score = specific ? toScore99(specific.score) : catScore99;
      return { id: t.id, label: i18n.t(t.labelKey), score, description: i18n.t(t.descriptionKey) };
    });

    const finalScore = catData.count > 0 ? toScore99(catData.total / catData.count) : 0;

    return { id: catId, name: i18n.t(meta.nameKey), shortName: meta.shortName, kpis: kpiDetails, score: finalScore };
  });

  const presentScores: number[] = [];
  for (const kpi of parsed) {
    const cat = oldToNewCategory[kpi.id] ?? newIdToCategory[kpi.id];
    if (cat) presentScores.push(kpi.score);
  }
  const globalScore = presentScores.length > 0
    ? toScore99(presentScores.reduce((s, x) => s + x, 0) / presentScores.length)
    : 0;

  return { globalScore, categories };
}
